import React from 'react';
import { RobbieMark, RobbieSize, RobbieState } from '../../components/ui';
import Section from './Section';

/**
 * /design — RobbieMark (TASK-103): every state × size, side by side with
 * `design/Design System.dc.html` section 05 and the v2 page mocks.
 */
const STATES: RobbieState[] = ['idle', 'listening', 'thinking', 'searching', 'insight', 'working', 'done', 'waving'];
const SIZES: RobbieSize[] = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'];

const SIZE_NOTES: Record<RobbieSize, string> = {
  xxs: 'badge / insight chip',
  xs: 'inline rows / correction bar',
  sm: 'chat avatar / panel header',
  md: 'nav logo',
  lg: 'spec tile',
  xl: 'login hero',
};

const Tile: React.FC<{ label: string; sub?: string; children: React.ReactNode }> = ({ label, sub, children }) => (
  <div className="flex flex-col items-center gap-s3 rounded-card bg-canvas px-s2 pb-s4 pt-s6">
    <div className="flex min-h-[96px] items-center justify-center">{children}</div>
    <div className="text-center">
      <div className="text-table-header uppercase text-ink-secondary">{label}</div>
      {sub && <div className="mt-[2px] text-meta text-ink-muted">{sub}</div>}
    </div>
  </div>
);

const RobbieGallery: React.FC = () => (
  <>
    <Section title="Robbie — the mark (states, lg spec size)">
      <div className="grid grid-cols-2 gap-s4 sm:grid-cols-3 lg:grid-cols-6">
        {STATES.map((state) => (
          <Tile key={state} label={state}>
            <RobbieMark state={state} size="lg" />
          </Tile>
        ))}
      </div>
      <p className="mt-s5 text-body-sm text-ink-secondary">
        States live in the eyes: white = idle · azure ring = listening · shimmer = thinking ·
        azure glow + raised hand + ping beacon = insight · arcs + hands up = done · wave = login.
        Hands appear at every size — scaled, never dropped. CSS-only; still under reduced motion.
      </p>
    </Section>

    <Section title="Robbie — every state × every size">
      <div className="flex flex-col gap-s4">
        {STATES.map((state) => (
          <div key={state} className="flex items-end gap-s6 border-b border-line-divider pb-s4 last:border-b-0 last:pb-0">
            <span className="w-20 shrink-0 pb-s2 text-table-header uppercase text-ink-muted">{state}</span>
            <div className="flex flex-wrap items-end gap-s7">
              {SIZES.map((size) => (
                <div key={size} className="flex flex-col items-center gap-s2">
                  <RobbieMark state={state} size={size} />
                  <span className="text-meta text-ink-muted">{size}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-s5 text-meta text-ink-muted">
        {SIZES.map((s) => `${s} = ${SIZE_NOTES[s]}`).join(' · ')}
      </p>
    </Section>

    <Section title="Robbie — beacon prop + navy glow">
      <div className="grid grid-cols-2 gap-s4 sm:grid-cols-4">
        <Tile label="beacon" sub="tip signal on idle">
          <RobbieMark size="lg" beacon />
        </Tile>
        <Tile label="beacon sm" sub="panel-header tip">
          <RobbieMark size="sm" beacon />
        </Tile>
        <div className="flex flex-col items-center gap-s3 rounded-card bg-chrome px-s2 pb-s4 pt-s6">
          <div className="flex min-h-[96px] items-center justify-center">
            <RobbieMark size="md" glow />
          </div>
          <div className="text-center">
            <div className="text-table-header uppercase text-chrome-text">glow</div>
            <div className="mt-[2px] text-meta text-chrome-label">logo on navy chrome</div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-s3 rounded-card bg-chrome px-s2 pb-s4 pt-s6">
          <div className="flex min-h-[96px] items-center justify-center">
            <RobbieMark size="xl" state="waving" glow />
          </div>
          <div className="text-center">
            <div className="text-table-header uppercase text-chrome-text">waving + glow</div>
            <div className="mt-[2px] text-meta text-chrome-label">login hero</div>
          </div>
        </div>
      </div>
    </Section>
  </>
);

export default RobbieGallery;
